import { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
    Dialog,
    Box,
    Typography,
    Stack,
    Button
} from '@mui/material'

const ImageCropDialog = ({ open, imageUrl, onClose, onConfirm }) => {
    const [scale, setScale] = useState(1)
    const [posX, setPosX] = useState(0)
    const [posY, setPosY] = useState(0)
    const canvasRef = useRef(null)
    const imageRef = useRef(null)
    const isDraggingRef = useRef(false)
    const dragStartRef = useRef({ x: 0, y: 0 })
    const posStartRef = useRef({ posX: 0, posY: 0 })

    const getBounds = (img, currentScale) => {
        if (!img) return { maxOffsetX: 0, maxOffsetY: 0, w: 700, h: 700 }
        const imgRatio = img.width / img.height
        let drawWidth, drawHeight

        if (imgRatio >= 1) {
            drawWidth = 700
            drawHeight = 700 / imgRatio
        } else {
            drawHeight = 700
            drawWidth = 700 * imgRatio
        }

        const w = drawWidth * currentScale
        const h = drawHeight * currentScale

        const maxOffsetX = Math.abs(700 - w) / 2
        const maxOffsetY = Math.abs(700 - h) / 2

        return { maxOffsetX, maxOffsetY, w, h }
    }

    // Reset settings when image changes
    useEffect(() => {
        if (open && imageUrl) {
            setScale(1)
            setPosX(0)
            setPosY(0)
            const img = new Image()
            img.onload = () => {
                imageRef.current = img
                draw()
            }
            img.src = imageUrl
        }
    }, [open, imageUrl])

    // Re-clamp position when scale changes
    useEffect(() => {
        if (!imageRef.current) return
        const { maxOffsetX, maxOffsetY } = getBounds(imageRef.current, scale)
        setPosX(prev => Math.min(Math.max(prev, -maxOffsetX), maxOffsetX))
        setPosY(prev => Math.min(Math.max(prev, -maxOffsetY), maxOffsetY))
    }, [scale])

    const draw = () => {
        const canvas = canvasRef.current
        const img = imageRef.current
        if (!canvas || !img) return

        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, 700, 700)

        // Draw white background
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, 700, 700)

        const { maxOffsetX, maxOffsetY, w, h } = getBounds(img, scale)

        // Clamp offsets
        const clampedX = Math.min(Math.max(posX, -maxOffsetX), maxOffsetX)
        const clampedY = Math.min(Math.max(posY, -maxOffsetY), maxOffsetY)

        // Center position + offsets
        const x = (700 - w) / 2 + clampedX
        const y = (700 - h) / 2 + clampedY

        ctx.drawImage(img, x, y, w, h)
    }

    useEffect(() => {
        draw()
    }, [scale, posX, posY])

    const getCoordinates = (e) => {
        if (e.touches && e.touches.length > 0) {
            return { x: e.touches[0].clientX, y: e.touches[0].clientY }
        }
        return { x: e.clientX, y: e.clientY }
    }

    const handleStart = (e) => {
        isDraggingRef.current = true
        const coords = getCoordinates(e)
        dragStartRef.current = coords
        posStartRef.current = { posX, posY }
    }

    const handleMove = (e) => {
        if (!isDraggingRef.current || !imageRef.current) return
        const coords = getCoordinates(e)
        const dx = coords.x - dragStartRef.current.x
        const dy = coords.y - dragStartRef.current.y
        
        // Scale coordinates (280px display vs 700px internal canvas size)
        const scaleFactor = 700 / 280
        const rawX = posStartRef.current.posX + dx * scaleFactor
        const rawY = posStartRef.current.posY + dy * scaleFactor

        const { maxOffsetX, maxOffsetY } = getBounds(imageRef.current, scale)
        setPosX(Math.min(Math.max(rawX, -maxOffsetX), maxOffsetX))
        setPosY(Math.min(Math.max(rawY, -maxOffsetY), maxOffsetY))
    }

    const handleEnd = () => {
        isDraggingRef.current = false
    }

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        const onWheel = (e) => {
            e.preventDefault()
            const zoomDelta = -e.deltaY * 0.002
            setScale(prev => Math.min(Math.max(prev + zoomDelta, 0.5), 4))
        }

        canvas.addEventListener('wheel', onWheel, { passive: false })
        return () => {
            canvas.removeEventListener('wheel', onWheel)
        }
    }, [open])

    const handleConfirm = () => {
        const canvas = canvasRef.current
        if (!canvas) return
        canvas.toBlob((blob) => {
            if (blob) {
                const croppedFile = new File([blob], 'machine_image.png', { type: 'image/png' })
                onConfirm(croppedFile, URL.createObjectURL(blob))
            }
        }, 'image/png')
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm">
            <Box p={3} display="flex" flexDirection="column" alignItems="center" sx={{ width: 400 }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', textAlign: 'center' }}>
                    Ajustar Imagen
                </Typography>
                
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2, textAlign: 'center', fontSize: '0.82rem' }}>
                    Arrastra la imagen para ajustar su posición o usa el control de zoom para acercar/alejar.
                </Typography>
                
                <Box sx={{ border: '2px solid #ccc', overflow: 'hidden', width: 280, height: 280, mb: 3, borderRadius: 2 }}>
                    <canvas
                        ref={canvasRef}
                        width={700}
                        height={700}
                        style={{ width: '100%', height: '100%', display: 'block', cursor: 'grab', touchAction: 'none' }}
                        onMouseDown={handleStart}
                        onMouseMove={handleMove}
                        onMouseUp={handleEnd}
                        onMouseLeave={handleEnd}
                        onTouchStart={handleStart}
                        onTouchMove={handleMove}
                        onTouchEnd={handleEnd}
                    />
                </Box>
                
                <Stack spacing={2} width="100%">
                    <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Zoom</Typography>
                            <Typography variant="caption" color="textSecondary">{scale.toFixed(2)}x</Typography>
                        </Box>
                        <input
                            type="range"
                            min="0.5"
                            max="4"
                            step="0.01"
                            value={scale}
                            onChange={(e) => setScale(parseFloat(e.target.value))}
                            style={{ width: '100%', cursor: 'pointer' }}
                        />
                    </Box>
                </Stack>
                
                <Box mt={4} display="flex" gap={2} justifyContent="flex-end" width="100%">
                    <Button variant="outlined" onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none' }}>
                        Cancelar
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleConfirm} sx={{ borderRadius: 2, textTransform: 'none' }}>
                        Confirmar
                    </Button>
                </Box>
            </Box>
        </Dialog>
    )
}

ImageCropDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    imageUrl: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired
}

export default ImageCropDialog
